import {
  type PointerEvent as ReactPointerEvent,
  useRef,
  useState,
} from "react";
import { Button } from "@/components/ui/button";
import { m } from "@/paraglide/messages";
import { downloadCanvas } from "./download";

const COLORS = [
  { id: "black", value: "#0a0a0a" },
  { id: "blue", value: "#1d4ed8" },
  { id: "red", value: "#dc2626" },
];

/**
 * Free-hand signature canvas (pointer events → works for mouse, pen and touch).
 * Exports a transparent PNG (only the strokes are in the bitmap; the white
 * surface is CSS only).
 */
export function DrawSignature() {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const drawing = useRef(false);
  const last = useRef<{ x: number; y: number } | null>(null);
  const [color, setColor] = useState(COLORS[0].value);
  const [width, setWidth] = useState(3);

  const point = (e: ReactPointerEvent<HTMLCanvasElement>) => {
    const c = canvasRef.current;
    if (!c) return { x: 0, y: 0 };
    const rect = c.getBoundingClientRect();
    return {
      x: (e.clientX - rect.left) * (c.width / rect.width),
      y: (e.clientY - rect.top) * (c.height / rect.height),
    };
  };

  const onDown = (e: ReactPointerEvent<HTMLCanvasElement>) => {
    drawing.current = true;
    last.current = point(e);
    e.currentTarget.setPointerCapture(e.pointerId);
  };

  const onMove = (e: ReactPointerEvent<HTMLCanvasElement>) => {
    if (!drawing.current || !last.current) return;
    const ctx = canvasRef.current?.getContext("2d");
    if (!ctx) return;
    const p = point(e);
    ctx.strokeStyle = color;
    ctx.lineWidth = width;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.beginPath();
    ctx.moveTo(last.current.x, last.current.y);
    ctx.lineTo(p.x, p.y);
    ctx.stroke();
    last.current = p;
  };

  const onUp = () => {
    drawing.current = false;
    last.current = null;
  };

  const clear = () => {
    const c = canvasRef.current;
    c?.getContext("2d")?.clearRect(0, 0, c.width, c.height);
  };

  const download = () => {
    if (canvasRef.current) downloadCanvas(canvasRef.current, "signature.png");
  };

  return (
    <div className="space-y-4">
      <canvas
        ref={canvasRef}
        width={700}
        height={250}
        aria-label={m.sig_canvas_label()}
        onPointerDown={onDown}
        onPointerMove={onMove}
        onPointerUp={onUp}
        onPointerLeave={onUp}
        className="w-full touch-none rounded-xl border border-border bg-white"
        style={{ aspectRatio: "700 / 250" }}
      />
      <div className="flex flex-wrap items-center gap-4">
        <div className="flex items-center gap-2">
          {COLORS.map((c) => (
            <button
              key={c.id}
              type="button"
              aria-label={c.id}
              aria-pressed={color === c.value}
              onClick={() => setColor(c.value)}
              className={`h-7 w-7 rounded-full border-2 ${
                color === c.value ? "border-primary" : "border-transparent"
              }`}
              style={{ background: c.value }}
            />
          ))}
        </div>
        <label className="flex items-center gap-2 text-sm text-muted-foreground">
          {m.sig_pen_width()}
          <input
            type="range"
            min={1}
            max={8}
            value={width}
            onChange={(e) => setWidth(Number(e.target.value))}
            className="accent-primary"
          />
        </label>
        <div className="ml-auto flex gap-2">
          <Button type="button" variant="outline" onClick={clear}>
            {m.sig_clear()}
          </Button>
          <Button type="button" onClick={download}>
            {m.sig_download()}
          </Button>
        </div>
      </div>
    </div>
  );
}
